import React, { useState, useContext, useEffect, useRef } from 'react';
import { 
  ArrowLeft, 
  CreditCard, 
  DollarSign, 
  ChevronRight, 
  AlertCircle, 
  Wallet, 
  RefreshCw, 
  Check, 
  PhoneCall,
  ChevronsRight 
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import AppContext from '../../context/AppContext';
import { database, ref, set, update } from '../../FirebaseConfig';

const Retrait = () => {
  const navigate = useNavigate();
  const { userInfos, id } = useContext(AppContext);
  const [montant, setMontant] = useState('');
  const [methode, setMethode] = useState('mobile');
  const [numero, setNumero] = useState(''); // Numéro WhatsApp
  const [numeroTransfert, setNumeroTransfert] = useState(''); // Numéro pour le transfert
  const [modePaiement, setModePaiement] = useState('orange-money-ci'); // Mode de paiement
  const [error, setError] = useState('');
  const [isBusy, setIsBusy] = useState(false);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [isAutoRetraitEnabled, setIsAutoRetraitEnabled] = useState(false);
  
  // Vérification du statut d'auto-retrait à chaque fois que userInfos change
  useEffect(() => {
    if (userInfos && userInfos.retraitAuto) {
      setIsAutoRetraitEnabled(userInfos.retraitAuto.active || false);
    }
  }, [userInfos]);
  
  // Solde disponible et limites basées sur le type de plan
  const soldeDisponible = userInfos?.type_plan === 'payant' 
    ? userInfos?.balancePayant || 0
    : userInfos?.balanceGratuit || 0;
  
  // Limites de retrait - minimum 100 FCFA
  const limiteMin = 100;
  const limiteMax = userInfos?.type_plan === 'payant' ? 500000 : 50000;
  
  // Afficher tous les plans de types sont autorisés à faire un retrait
  useEffect(() => {
    console.log("Retrait - userInfos:", userInfos);
    // Retrait est maintenant disponible pour tous les types de plans
  }, [userInfos]);
  
  // Valider la demande de retrait
  const validerRetrait = (e) => {
    // Empêcher le comportement par défaut du formulaire
    if (e) e.preventDefault();

    setError('');
    
    // Vérifier si le montant est numérique
    const montantNum = parseFloat(montant);
    if (isNaN(montantNum) || montantNum <= 0) {
      setError('Veuillez saisir un montant valide');
      return;
    }
    
    // Vérifier les limites
    if (montantNum < limiteMin) {
      setError(`Le montant minimum de retrait est de ${limiteMin} FCFA`);
      return;
    }
    
    if (montantNum > limiteMax) {
      setError(`Le montant maximum de retrait est de ${limiteMax} FCFA`);
      return;
    }
    
    if (montantNum > soldeDisponible) {
      setError('Solde insuffisant pour ce retrait');
      return;
    }
    
    // Valider le numéro WhatsApp (minimum 6 chiffres)
    if (!numero || numero.length < 6) {
      setError('Veuillez saisir un numéro WhatsApp valide (minimum 6 chiffres)');
      return;
    }
    
    // Valider le numéro de transfert (minimum 6 chiffres)
    if (!numeroTransfert || numeroTransfert.length < 6) {
      setError('Veuillez saisir un numéro pour le transfert valide (minimum 6 chiffres)');
      return;
    }
    
    // Valider le mode de paiement
    if (!modePaiement) {
      setError('Veuillez sélectionner un mode de paiement');
      return;
    }
    
    // Tout est valide, ouvrir la confirmation
    setIsConfirmOpen(true);
  };
  
  // Soumettre la demande de retrait
  const soumettreRetrait = async () => {
    setIsBusy(true);
    
    try {
      // Calculer le montant numériquement
      const montantNum = parseFloat(montant);
      
      // Vérifier le solde disponible
      if (montantNum > soldeDisponible) {
        setError('Solde insuffisant pour ce retrait');
        setIsConfirmOpen(false);
        return;
      }
      
      // Créer l'objet de retrait
      const demandeId = Date.now().toString();
      const retraitData = {
        id: demandeId,
        montant: montantNum,
        methode: methode,
        numeroWhatsapp: numero,
        numeroTransfert: numeroTransfert,
        modePaiement: modePaiement,
        statut: 'en_attente',
        date: new Date().toISOString(),
        userId: id
      };
      
      // Enregistrer dans Firebase - nouvelle structure
      const retraitRef = ref(database, `demandesretrait/${id}/${demandeId}`);
      await set(retraitRef, retraitData);
      
      // Également ajouter à l'ancienne collection pour compatibilité
      const oldRetraitRef = ref(database, `retraits/${demandeId}_${id}`);
      await set(oldRetraitRef, retraitData);
      
      // Ne pas mettre à jour le solde ici - il sera mis à jour par l'admin lorsque la demande sera validée
      
      // Traitement réussi
      setIsConfirmOpen(false);
      setIsSuccess(true);
    } catch (error) {
      console.error("Erreur lors du traitement du retrait:", error);
      setError('Une erreur est survenue lors du traitement de votre demande');
      setIsConfirmOpen(false);
    } finally {
      setIsBusy(false);
    }
  };
  
  // Composant pour les options de méthode de retrait
  const MethodeOption = ({ type, label, icon, description }) => (
    <div 
      onClick={() => setMethode(type)}
      className={`flex items-center p-4 border rounded-xl cursor-pointer transition-all ${
        methode === type 
          ? 'border-blue-500 bg-blue-50' 
          : 'border-gray-200 hover:border-gray-300'
      }`}
    >
      <div className={`p-2 rounded-lg mr-3 ${
        methode === type ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-600'
      }`}>
        {icon}
      </div>
      <div className="flex-1">
        <div className="flex items-center justify-between">
          <h3 className="font-medium text-gray-900">{label}</h3>
          {methode === type && <Check className="w-4 h-4 text-blue-500" />}
        </div>
        <p className="text-sm text-gray-500">{description}</p>
      </div>
    </div>
  );
  
  // Pas de bottom sheet - nous utilisons une page dédiée à la place

  // Modal de confirmation
  const ConfirmationModal = () => (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-xl max-w-md w-full overflow-hidden">
        <div className="p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Confirmer le retrait</h3>
          <div className="space-y-4 mb-6">
            <div className="flex justify-between py-2 border-b border-gray-100">
              <span className="text-gray-600">Montant</span>
              <span className="font-semibold">{parseFloat(montant).toLocaleString()} FCFA</span>
            </div>
            <div className="flex justify-between py-2 border-b border-gray-100">
              <span className="text-gray-600">Méthode</span>
              <span className="font-semibold">
                WhatsApp
              </span>
            </div>
            <div className="flex justify-between py-2 border-b border-gray-100">
              <span className="text-gray-600">Numéro WhatsApp</span>
              <span className="font-semibold">{numero}</span>
            </div>
            <div className="flex justify-between py-2 border-b border-gray-100">
              <span className="text-gray-600">Nom</span>
              <span className="font-semibold">--</span>
            </div>
            <div className="flex justify-between py-2 border-b border-gray-100">
              <span className="text-gray-600">Numéro de transfert</span>
              <span className="font-semibold">{numeroTransfert}</span>
            </div>
            <div className="flex justify-between py-2 border-b border-gray-100">
              <span className="text-gray-600">Mode de paiement</span>
              <span className="font-semibold">
                {(() => {
                  const paymentMethods = {
                    'orange-money-senegal': 'Orange Money Sénégal',
                    'free-money-senegal': 'Free Money Sénégal',
                    'expresso-senegal': 'E-Money Sénégal',
                    'wave-senegal': 'Wave Sénégal',
                    'mtn-benin': 'MTN Bénin',
                    'moov-benin': 'Moov Bénin',
                    'mtn-ci': 'MTN Côte d\'Ivoire',
                    'orange-money-ci': 'Orange Money CI',
                    'moov-ci': 'Moov Côte d\'Ivoire',
                    'wave-ci': 'Wave Côte d\'Ivoire',
                    't-money-togo': 'T-Money Togo',
                    'moov-togo': 'Moov Togo',
                    'orange-money-mali': 'Orange Money Mali',
                    'orange-money-burkina': 'Orange Money Burkina',
                    'moov-burkina-faso': 'Moov Burkina'
                  };
                  return paymentMethods[modePaiement] || modePaiement;
                })()}
              </span>
            </div>
            <div className="flex justify-between py-2 border-b border-gray-100">
              <span className="text-gray-600">Pays</span>
              <span className="font-semibold">
                {(() => {
                  const countryMap = {
                    'orange-money-senegal': 'Sénégal',
                    'free-money-senegal': 'Sénégal',
                    'expresso-senegal': 'Sénégal',
                    'wave-senegal': 'Sénégal',
                    'mtn-benin': 'Bénin',
                    'moov-benin': 'Bénin',
                    'mtn-ci': 'Côte d\'Ivoire',
                    'orange-money-ci': 'Côte d\'Ivoire',
                    'moov-ci': 'Côte d\'Ivoire',
                    'wave-ci': 'Côte d\'Ivoire',
                    't-money-togo': 'Togo',
                    'moov-togo': 'Togo',
                    'orange-money-mali': 'Mali',
                    'orange-money-burkina': 'Burkina Faso',
                    'moov-burkina-faso': 'Burkina Faso'
                  };
                  return countryMap[modePaiement] || '--';
                })()}
              </span>
            </div>
            <div className="flex justify-between py-2 border-b border-gray-100">
              <span className="text-gray-600">Frais</span>
              <span className="font-semibold">0 FCFA</span>
            </div>
            <div className="flex justify-between py-2">
              <span className="text-gray-600">Total à recevoir</span>
              <span className="font-semibold text-blue-600">{parseFloat(montant).toLocaleString()} FCFA</span>
            </div>
          </div>
          
          <div className="bg-yellow-50 border border-yellow-100 rounded-xl p-3 mb-6">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-yellow-500 mt-0.5" />
              <p className="text-sm text-yellow-700">
                Le traitement des retraits peut prendre jusqu'à 24 heures. Vous recevrez une notification une fois le retrait effectué.
              </p>
            </div>
          </div>
          
          <div className="flex gap-3">
            <button 
              onClick={() => setIsConfirmOpen(false)}
              className="flex-1 py-3 text-gray-700 bg-gray-100 rounded-xl font-medium"
              disabled={isBusy}
            >
              Annuler
            </button>
            <button 
              onClick={soumettreRetrait}
              className="flex-1 py-3 text-white bg-blue-600 rounded-xl font-medium flex items-center justify-center gap-2"
              disabled={isBusy}
            >
              {isBusy ? (
                <>
                  <RefreshCw className="w-4 h-4 animate-spin" />
                  Traitement...
                </>
              ) : (
                'Confirmer'
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
  
  // Bottom sheet de confirmation de la demande de retrait
  const SuccessBottomSheet = () => (
    <div className="fixed inset-0 z-50">
      {/* Overlay avec animation de fade in */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm"></div>
      
      {/* Bottom Sheet de succès */}
      <div className="absolute bottom-0 left-0 right-0 bg-white rounded-t-3xl shadow-2xl p-6">
        <div className="text-center py-6">
          <div className="bg-green-100 text-green-600 rounded-full p-4 w-20 h-20 mx-auto mb-6 flex items-center justify-center">
            <Check className="w-10 h-10" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-3">Demande enregistrée</h1>
          <p className="text-gray-600 mb-3">
            Votre demande de retrait de <span className="font-semibold">{parseFloat(montant).toLocaleString()} FCFA</span> a été enregistrée.
          </p>
          
          <div className="bg-blue-50 border border-blue-100 rounded-xl p-4 mb-2">
            <p className="text-sm text-blue-700">
              Votre solde sera débité uniquement après validation de votre demande par notre équipe.
            </p>
          </div>
          
          <div className="bg-yellow-50 border border-yellow-100 rounded-xl p-4 mb-6">
            <p className="text-sm text-yellow-700">
              Un agent vous contactera sur WhatsApp au {numero} pour finaliser le transfert. Veuillez rester attentif aux messages.
            </p>
          </div>
          
          <button 
            onClick={() => navigate('/IndexEspace')}
            className="w-full py-4 bg-blue-600 text-white rounded-xl font-medium shadow-sm"
          >
            Retourner à l'accueil
          </button>
        </div>
      </div>
    </div>
  );
  
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
     
      {/* Contenu principal */}
      <div className="px-4 py-6">
        {/* Statut de paiement automatique */}
        <div className="mb-5 bg-white rounded-xl shadow-sm overflow-hidden">
          <div className="p-4 flex items-start justify-between">
            <div className="flex-grow">
              <div className="flex items-center gap-2 mb-2">
                <h3 className="text-base font-semibold text-gray-800">Paiements automatiques</h3>
                {isAutoRetraitEnabled && (
                  <div className="bg-green-50 border border-green-200 rounded-full py-0.5 px-2 flex items-center">
                    <div className="w-2 h-2 bg-green-500 rounded-full mr-1"></div>
                    <span className="text-xs font-medium text-green-700">Activé</span>
                  </div>
                )}
              </div>
              
              <p className="text-sm text-gray-600 mb-1">
                Recevez vos paiements instantanément sur votre compte Mobile Money dès qu'ils sont effectués.
              </p>
              
              {isAutoRetraitEnabled ? (
                <div className="flex items-center gap-2 mt-2">
                  <div className="px-2.5 py-1 bg-gray-100 rounded-md text-xs font-medium text-gray-700 flex items-center">
                    {(() => {
                      const paymentMode = userInfos?.retraitAuto?.modePaiement || modePaiement;
                      
                      // Emoji flags mapping
                      const countryEmoji = 
                        paymentMode.includes('ci') ? '🇨🇮' :
                        paymentMode.includes('benin') ? '🇧🇯' :
                        paymentMode.includes('senegal') ? '🇸🇳' :
                        paymentMode.includes('togo') ? '🇹🇬' :
                        paymentMode.includes('mali') ? '🇲🇱' :
                        paymentMode.includes('burkina') ? '🇧🇫' :
                        '';
                      
                      // Payment method formatting
                      const paymentMethods = {
                        'orange-money-senegal': 'Orange Money',
                        'free-money-senegal': 'Free Money',
                        'expresso-senegal': 'E-Money',
                        'wave-senegal': 'Wave',
                        'mtn-benin': 'MTN',
                        'moov-benin': 'Moov',
                        'mtn-ci': 'MTN',
                        'orange-money-ci': 'Orange Money',
                        'moov-ci': 'Moov',
                        'wave-ci': 'Wave',
                        't-money-togo': 'T-Money',
                        'moov-togo': 'Moov',
                        'orange-money-mali': 'Orange Money',
                        'orange-money-burkina': 'Orange Money',
                        'moov-burkina-faso': 'Moov'
                      };
                      
                      return (
                        <>
                          <span className="mr-1.5 text-base">{countryEmoji}</span>
                          {paymentMethods[paymentMode] || paymentMode}
                        </>
                      );
                    })()}
                  </div>
                  <span className="text-xs text-gray-500">•</span>
                  <span className="text-xs text-gray-500">
                    {userInfos?.retraitAuto?.numeroTransfert || numeroTransfert}
                  </span>
                </div>
              ) : (
                <p className="text-sm text-gray-500 italic mt-1">
                  Non configuré
                </p>
              )}
            </div>
            
            <button 
              onClick={() => navigate('/ConfigurationRetrait')}
              className={`px-3.5 py-2 rounded-lg text-sm font-medium ${
                isAutoRetraitEnabled 
                  ? 'bg-indigo-50 text-indigo-700 hover:bg-indigo-100' 
                  : 'bg-indigo-600 text-white hover:bg-indigo-700'
              } transition-colors shadow-sm whitespace-nowrap`}
            >
              {isAutoRetraitEnabled ? 'Modifier' : 'Activer'}
            </button>
          </div>
        </div>
        
        {/* Carte de solde */}
        <div className="bg-gradient-to-br from-blue-600 to-indigo-700 rounded-2xl p-5 mb-6">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center">
              <div className="bg-white/20 p-2 rounded-lg mr-3">
                <Wallet className="w-5 h-5 text-white" />
              </div>
              <p className="text-white text-sm">Solde disponible</p>
            </div>
          </div>
          <h2 className="text-3xl font-bold text-white mb-1 flex items-baseline">
            {soldeDisponible.toLocaleString()}
            <span className="text-sm font-medium text-blue-100 ml-2">FCFA</span>
          </h2>
        </div>
        
        {/* Formulaire de retrait */}
        <div className="bg-white rounded-2xl shadow-sm mb-6">
          <div className="p-5">
            <h2 className="text-lg font-semibold text-gray-900 mb-5">Informations de retrait</h2>
            
            {/* Montant */}
            <div className="mb-5">
              <label htmlFor="montant" className="block text-sm font-medium text-gray-700 mb-2">
                Montant à retirer
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <DollarSign className="w-5 h-5 text-gray-400" />
                </div>
                <input
                  type="number"
                  id="montant"
                  value={montant}
                  onChange={(e) => setMontant(e.target.value)}
                  placeholder="Saisir le montant"
                  className="block w-full pl-12 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-blue-500 focus:border-blue-500"
                />
                <div className="absolute inset-y-0 right-0 pr-4 flex items-center pointer-events-none">
                  <span className="text-gray-500">FCFA</span>
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-2">
                Montant minimum: {limiteMin.toLocaleString()} FCFA, maximum: {limiteMax.toLocaleString()} FCFA
              </p>
            </div>
            
            {/* Méthode de retrait */}
            <div className="mb-5">
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Méthode de retrait
              </label>
              <div className="p-4 rounded-xl bg-blue-50 flex items-center">
                <div className="p-3 bg-blue-100 text-blue-600 rounded-xl mr-3">
                  <PhoneCall className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="font-medium text-gray-900">Par WhatsApp</h3>
                  <p className="text-sm text-gray-600">
                    Un agent vous contactera sur WhatsApp pour finaliser la demande de transfert
                  </p>
                </div>
              </div>
            </div>
            
            {/* Numéro WhatsApp */}
            <div className="mb-5">
              <label htmlFor="numero" className="block text-sm font-medium text-gray-700 mb-2">
                Numéro WhatsApp
              </label>
              <input
                type="tel"
                id="numero"
                value={numero}
                onChange={(e) => setNumero(e.target.value)}
                placeholder="Ex: 071234"
                className="block w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-blue-500 focus:border-blue-500"
              />
              <p className="text-xs text-gray-500 mt-1">Ce numéro servira à vous contacter pour la demande de transfert</p>
            </div>
            
            {/* Nom */}
            {/* Section nom supprimée */}
            
            {/* Numéro pour le transfert */}
            <div className="mb-5">
              <label htmlFor="numeroTransfert" className="block text-sm font-medium text-gray-700 mb-2">
                Numéro pour recevoir le transfert
              </label>
              <input
                type="tel"
                id="numeroTransfert"
                value={numeroTransfert}
                onChange={(e) => setNumeroTransfert(e.target.value)}
                placeholder="Ex: 071234"
                className="block w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-blue-500 focus:border-blue-500"
              />
              <p className="text-xs text-gray-500 mt-1">Ce numéro recevra le transfert d'argent</p>
            </div>
            
            {/* Mode de paiement */}
            <div className="mb-5">
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Mode de paiement
              </label>
              {/* Organisation par pays avec tabs */}
              <div className="mb-3 relative">
                {/* Indicateur de défilement à droite */}
                <div className="absolute right-0 top-0 bottom-0 w-8 bg-gradient-to-l from-gray-50 to-transparent z-10 flex items-center justify-center pointer-events-none">
                  <div className="animate-bounce-x">
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-gray-400">
                      <polyline points="9 18 15 12 9 6"></polyline>
                    </svg>
                  </div>
                </div>
                
                <div className="flex overflow-x-auto pb-2 gap-2 border-b border-gray-200 scrollbar-hide relative">
                  {[
                    { id: 'ci', name: 'Côte d\'Ivoire', flag: '🇨🇮' },
                    { id: 'senegal', name: 'Sénégal', flag: '🇸🇳' },
                    { id: 'benin', name: 'Bénin', flag: '🇧🇯' },
                    { id: 'togo', name: 'Togo', flag: '🇹🇬' },
                    { id: 'mali', name: 'Mali', flag: '🇲🇱' },
                    { id: 'burkina', name: 'Burkina', flag: '🇧🇫' }
                  ].map((country, index) => {
                    const isActive = (
                      (country.id === 'ci' && modePaiement.includes('-ci')) ||
                      (country.id === 'senegal' && modePaiement.includes('senegal')) ||
                      (country.id === 'benin' && modePaiement.includes('benin')) ||
                      (country.id === 'togo' && modePaiement.includes('togo')) ||
                      (country.id === 'mali' && modePaiement.includes('mali')) ||
                      (country.id === 'burkina' && modePaiement.includes('burkina'))
                    );
                    
                    return (
                      <button
                        key={country.id}
                        onClick={() => {
                          // Sélectionner le premier opérateur de ce pays
                          const operators = {
                            'ci': 'orange-money-ci',
                            'senegal': 'orange-money-senegal',
                            'benin': 'mtn-benin',
                            'togo': 't-money-togo',
                            'mali': 'orange-money-mali',
                            'burkina': 'orange-money-burkina'
                          };
                          setModePaiement(operators[country.id]);
                        }}
                        className={`px-4 py-2 rounded-t-lg flex-shrink-0 flex items-center min-w-max ${
                          isActive 
                            ? 'bg-blue-50 text-blue-600 font-medium border-b-2 border-blue-500' 
                            : 'text-gray-600 hover:bg-gray-50'
                        }`}
                      >
                        <span className="mr-2">{country.flag}</span>
                        {country.name}
                      </button>
                    );
                  })}
                </div>
                
                {/* Style pour cacher la scrollbar mais permettre le défilement */}
                <style jsx>{`
                  .scrollbar-hide {
                    -ms-overflow-style: none;  /* IE and Edge */
                    scrollbar-width: none;  /* Firefox */
                  }
                  .scrollbar-hide::-webkit-scrollbar {
                    display: none;  /* Chrome, Safari, Opera */
                  }
                  @keyframes bounce-x {
                    0%, 100% {
                      transform: translateX(-25%);
                      animation-timing-function: cubic-bezier(0.8, 0, 1, 1);
                    }
                    50% {
                      transform: translateX(0);
                      animation-timing-function: cubic-bezier(0, 0, 0.2, 1);
                    }
                  }
                  .animate-bounce-x {
                    animation: bounce-x 1s infinite;
                  }
                `}</style>
              </div>

              {/* Côte d'Ivoire */}
              {modePaiement.includes('-ci') && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <div 
                    onClick={() => setModePaiement('orange-money-ci')}
                    className={`flex items-center p-3 border rounded-xl cursor-pointer ${modePaiement === 'orange-money-ci' ? 'border-orange-500 bg-orange-50 shadow-sm' : 'border-gray-200'}`}
                  >
                    <div className={`w-5 h-5 rounded-full border-2 mr-2 flex items-center justify-center ${modePaiement === 'orange-money-ci' ? 'border-orange-500' : 'border-gray-300'}`}>
                      {modePaiement === 'orange-money-ci' && <div className="w-3 h-3 bg-orange-500 rounded-full"></div>}
                    </div>
                    <span className="mr-2 text-lg">🇨🇮</span>
                    <span className="font-medium">Orange Money CI</span>
                  </div>
                  
                  <div 
                    onClick={() => setModePaiement('mtn-ci')}
                    className={`flex items-center p-3 border rounded-xl cursor-pointer ${modePaiement === 'mtn-ci' ? 'border-yellow-500 bg-yellow-50 shadow-sm' : 'border-gray-200'}`}
                  >
                    <div className={`w-5 h-5 rounded-full border-2 mr-2 flex items-center justify-center ${modePaiement === 'mtn-ci' ? 'border-yellow-500' : 'border-gray-300'}`}>
                      {modePaiement === 'mtn-ci' && <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>}
                    </div>
                    <span className="mr-2 text-lg">🇨🇮</span>
                    <span className="font-medium">MTN CI</span>
                  </div>
                  
                  <div 
                    onClick={() => setModePaiement('moov-ci')}
                    className={`flex items-center p-3 border rounded-xl cursor-pointer ${modePaiement === 'moov-ci' ? 'border-blue-500 bg-blue-50 shadow-sm' : 'border-gray-200'}`}
                  >
                    <div className={`w-5 h-5 rounded-full border-2 mr-2 flex items-center justify-center ${modePaiement === 'moov-ci' ? 'border-blue-500' : 'border-gray-300'}`}>
                      {modePaiement === 'moov-ci' && <div className="w-3 h-3 bg-blue-500 rounded-full"></div>}
                    </div>
                    <span className="mr-2 text-lg">🇨🇮</span>
                    <span className="font-medium">Moov CI</span>
                  </div>
                  
                  <div 
                    onClick={() => setModePaiement('wave-ci')}
                    className={`flex items-center p-3 border rounded-xl cursor-pointer ${modePaiement === 'wave-ci' ? 'border-teal-500 bg-teal-50 shadow-sm' : 'border-gray-200'}`}
                  >
                    <div className={`w-5 h-5 rounded-full border-2 mr-2 flex items-center justify-center ${modePaiement === 'wave-ci' ? 'border-teal-500' : 'border-gray-300'}`}>
                      {modePaiement === 'wave-ci' && <div className="w-3 h-3 bg-teal-500 rounded-full"></div>}
                    </div>
                    <span className="mr-2 text-lg">🇨🇮</span>
                    <span className="font-medium">Wave CI</span>
                  </div>
                </div>
              )}
              
              {/* Sénégal */}
              {modePaiement.includes('senegal') && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <div 
                    onClick={() => setModePaiement('orange-money-senegal')}
                    className={`flex items-center p-3 border rounded-xl cursor-pointer ${modePaiement === 'orange-money-senegal' ? 'border-orange-500 bg-orange-50 shadow-sm' : 'border-gray-200'}`}
                  >
                    <div className={`w-5 h-5 rounded-full border-2 mr-2 flex items-center justify-center ${modePaiement === 'orange-money-senegal' ? 'border-orange-500' : 'border-gray-300'}`}>
                      {modePaiement === 'orange-money-senegal' && <div className="w-3 h-3 bg-orange-500 rounded-full"></div>}
                    </div>
                    <span className="mr-2 text-lg">🇸🇳</span>
                    <span className="font-medium">Orange Money</span>
                  </div>
                  
                  <div 
                    onClick={() => setModePaiement('free-money-senegal')}
                    className={`flex items-center p-3 border rounded-xl cursor-pointer ${modePaiement === 'free-money-senegal' ? 'border-purple-500 bg-purple-50 shadow-sm' : 'border-gray-200'}`}
                  >
                    <div className={`w-5 h-5 rounded-full border-2 mr-2 flex items-center justify-center ${modePaiement === 'free-money-senegal' ? 'border-purple-500' : 'border-gray-300'}`}>
                      {modePaiement === 'free-money-senegal' && <div className="w-3 h-3 bg-purple-500 rounded-full"></div>}
                    </div>
                    <span className="mr-2 text-lg">🇸🇳</span>
                    <span className="font-medium">Free Money</span>
                  </div>
                  
                  <div 
                    onClick={() => setModePaiement('expresso-senegal')}
                    className={`flex items-center p-3 border rounded-xl cursor-pointer ${modePaiement === 'expresso-senegal' ? 'border-red-500 bg-red-50 shadow-sm' : 'border-gray-200'}`}
                  >
                    <div className={`w-5 h-5 rounded-full border-2 mr-2 flex items-center justify-center ${modePaiement === 'expresso-senegal' ? 'border-red-500' : 'border-gray-300'}`}>
                      {modePaiement === 'expresso-senegal' && <div className="w-3 h-3 bg-red-500 rounded-full"></div>}
                    </div>
                    <span className="mr-2 text-lg">🇸🇳</span>
                    <span className="font-medium">E-Money</span>
                  </div>
                  
                  <div 
                    onClick={() => setModePaiement('wave-senegal')}
                    className={`flex items-center p-3 border rounded-xl cursor-pointer ${modePaiement === 'wave-senegal' ? 'border-teal-500 bg-teal-50 shadow-sm' : 'border-gray-200'}`}
                  >
                    <div className={`w-5 h-5 rounded-full border-2 mr-2 flex items-center justify-center ${modePaiement === 'wave-senegal' ? 'border-teal-500' : 'border-gray-300'}`}>
                      {modePaiement === 'wave-senegal' && <div className="w-3 h-3 bg-teal-500 rounded-full"></div>}
                    </div>
                    <span className="mr-2 text-lg">🇸🇳</span>
                    <span className="font-medium">Wave</span>
                  </div>
                </div>
              )}
              
              {/* Bénin */}
              {modePaiement.includes('benin') && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <div 
                    onClick={() => setModePaiement('mtn-benin')}
                    className={`flex items-center p-3 border rounded-xl cursor-pointer ${modePaiement === 'mtn-benin' ? 'border-yellow-500 bg-yellow-50 shadow-sm' : 'border-gray-200'}`}
                  >
                    <div className={`w-5 h-5 rounded-full border-2 mr-2 flex items-center justify-center ${modePaiement === 'mtn-benin' ? 'border-yellow-500' : 'border-gray-300'}`}>
                      {modePaiement === 'mtn-benin' && <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>}
                    </div>
                    <span className="mr-2 text-lg">🇧🇯</span>
                    <span className="font-medium">MTN Bénin</span>
                  </div>
                  
                  <div 
                    onClick={() => setModePaiement('moov-benin')}
                    className={`flex items-center p-3 border rounded-xl cursor-pointer ${modePaiement === 'moov-benin' ? 'border-blue-500 bg-blue-50 shadow-sm' : 'border-gray-200'}`}
                  >
                    <div className={`w-5 h-5 rounded-full border-2 mr-2 flex items-center justify-center ${modePaiement === 'moov-benin' ? 'border-blue-500' : 'border-gray-300'}`}>
                      {modePaiement === 'moov-benin' && <div className="w-3 h-3 bg-blue-500 rounded-full"></div>}
                    </div>
                    <span className="mr-2 text-lg">🇧🇯</span>
                    <span className="font-medium">Moov Bénin</span>
                  </div>
                </div>
              )}
              
              {/* Togo */}
              {modePaiement.includes('togo') && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <div 
                    onClick={() => setModePaiement('t-money-togo')}
                    className={`flex items-center p-3 border rounded-xl cursor-pointer ${modePaiement === 't-money-togo' ? 'border-green-500 bg-green-50 shadow-sm' : 'border-gray-200'}`}
                  >
                    <div className={`w-5 h-5 rounded-full border-2 mr-2 flex items-center justify-center ${modePaiement === 't-money-togo' ? 'border-green-500' : 'border-gray-300'}`}>
                      {modePaiement === 't-money-togo' && <div className="w-3 h-3 bg-green-500 rounded-full"></div>}
                    </div>
                    <span className="mr-2 text-lg">🇹🇬</span>
                    <span className="font-medium">T-Money Togo</span>
                  </div>
                  
                  <div 
                    onClick={() => setModePaiement('moov-togo')}
                    className={`flex items-center p-3 border rounded-xl cursor-pointer ${modePaiement === 'moov-togo' ? 'border-blue-500 bg-blue-50 shadow-sm' : 'border-gray-200'}`}
                  >
                    <div className={`w-5 h-5 rounded-full border-2 mr-2 flex items-center justify-center ${modePaiement === 'moov-togo' ? 'border-blue-500' : 'border-gray-300'}`}>
                      {modePaiement === 'moov-togo' && <div className="w-3 h-3 bg-blue-500 rounded-full"></div>}
                    </div>
                    <span className="mr-2 text-lg">🇹🇬</span>
                    <span className="font-medium">Moov Togo</span>
                  </div>
                </div>
              )}
              
              {/* Mali */}
              {modePaiement.includes('mali') && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <div 
                    onClick={() => setModePaiement('orange-money-mali')}
                    className={`flex items-center p-3 border rounded-xl cursor-pointer ${modePaiement === 'orange-money-mali' ? 'border-orange-500 bg-orange-50 shadow-sm' : 'border-gray-200'}`}
                  >
                    <div className={`w-5 h-5 rounded-full border-2 mr-2 flex items-center justify-center ${modePaiement === 'orange-money-mali' ? 'border-orange-500' : 'border-gray-300'}`}>
                      {modePaiement === 'orange-money-mali' && <div className="w-3 h-3 bg-orange-500 rounded-full"></div>}
                    </div>
                    <span className="mr-2 text-lg">🇲🇱</span>
                    <span className="font-medium">Orange Money Mali</span>
                  </div>
                </div>
              )}
              
              {/* Burkina */}
              {modePaiement.includes('burkina') && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <div 
                    onClick={() => setModePaiement('orange-money-burkina')}
                    className={`flex items-center p-3 border rounded-xl cursor-pointer ${modePaiement === 'orange-money-burkina' ? 'border-orange-500 bg-orange-50 shadow-sm' : 'border-gray-200'}`}
                  >
                    <div className={`w-5 h-5 rounded-full border-2 mr-2 flex items-center justify-center ${modePaiement === 'orange-money-burkina' ? 'border-orange-500' : 'border-gray-300'}`}>
                      {modePaiement === 'orange-money-burkina' && <div className="w-3 h-3 bg-orange-500 rounded-full"></div>}
                    </div>
                    <span className="mr-2 text-lg">🇧🇫</span>
                    <span className="font-medium">Orange Money Burkina</span>
                  </div>
                  
                  <div 
                    onClick={() => setModePaiement('moov-burkina-faso')}
                    className={`flex items-center p-3 border rounded-xl cursor-pointer ${modePaiement === 'moov-burkina-faso' ? 'border-blue-500 bg-blue-50 shadow-sm' : 'border-gray-200'}`}
                  >
                    <div className={`w-5 h-5 rounded-full border-2 mr-2 flex items-center justify-center ${modePaiement === 'moov-burkina-faso' ? 'border-blue-500' : 'border-gray-300'}`}>
                      {modePaiement === 'moov-burkina-faso' && <div className="w-3 h-3 bg-blue-500 rounded-full"></div>}
                    </div>
                    <span className="mr-2 text-lg">🇧🇫</span>
                    <span className="font-medium">Moov Burkina</span>
                  </div>
                </div>
              )}
            </div>
            
            {/* Section pays supprimée */}
            
            {/* Message d'erreur */}
            {error && (
              <div className="bg-red-50 border border-red-100 rounded-xl p-3 mb-5">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-red-500 mt-0.5" />
                  <p className="text-sm text-red-700">{error}</p>
                </div>
              </div>
            )}
            
            {/* Bouton de soumission */}
            <button
              onClick={validerRetrait}
              type="button"
              className="w-full bg-blue-600 text-white py-3 rounded-xl font-medium hover:bg-blue-700 transition-colors"
            >
              Continuer
            </button>
          </div>
        </div>
        
        {/* Informations complémentaires */}
        <div className="bg-blue-50 border border-blue-100 rounded-xl p-4">
          <div className="flex items-start gap-3">
            <div className="p-1 bg-blue-100 rounded-full text-blue-600 mt-0.5">
              <AlertCircle className="w-4 h-4" />
            </div>
            <div>
              <p className="text-sm text-blue-700 mb-2">
                Les demandes de retrait sont traitées du lundi au vendredi, de 9h à 17h.
              </p>
              <p className="text-sm text-blue-700">
                Un agent vous contactera sur WhatsApp pour finaliser votre demande de retrait.
                Assurez-vous que le numéro WhatsApp fourni est correct.
              </p>
            </div>
          </div>
        </div>
      </div>
      
      {/* Modal de confirmation */}
      {isConfirmOpen && <ConfirmationModal />}
      
      {/* Bottom Sheet de succès */}
      {isSuccess && <SuccessBottomSheet />}
    </div>
  );
};

export default Retrait;